import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Search, Users, Building2, Mail, Phone,
  ChevronRight, FileText
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function MobileCustomers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const token = localStorage.getItem('token');

  const fetchCustomers = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filteredCustomers = customers.filter(customer => 
    !searchQuery || 
    customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.org_number?.includes(searchQuery) ||
    customer.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m/more')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('customers.title')}</div>
        </div>
        <button className="mobile-header-btn" onClick={() => navigate('/m/customers/new')}>
          <Plus size={20} />
        </button>
      </header>

      {/* Content */}
      <main className="mobile-content">
        {/* Stats Card */}
        <div className="mobile-card" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: '#e0f2fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Users size={28} color="#0284c7" />
          </div>
          <div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e3a5f' }}>{customers.length}</div>
            <div style={{ color: '#64748b', fontSize: '14px' }}>{t('customers.registered')}</div>
          </div>
        </div>

        {/* Search */}
        <div className="mobile-search">
          <Search size={18} className="mobile-search-icon" />
          <input
            type="text"
            className="mobile-search-input"
            placeholder={`${t('common.search')}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Customer List */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="mobile-spinner" />
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="mobile-empty">
            <div className="mobile-empty-icon"><Users size={48} /></div>
            <div className="mobile-empty-title">{t('common.noData')}</div>
            <div className="mobile-empty-desc">{t('customers.newCustomer')}</div>
            <button 
              className="mobile-btn mobile-btn-primary" 
              style={{ marginTop: '16px', width: 'auto', padding: '12px 24px' }}
              onClick={() => navigate('/m/customers/new')}
            >
              <Plus size={18} />
              {t('customers.addCustomer')}
            </button>
          </div>
        ) : (
          <div className="mobile-list">
            {filteredCustomers.map((customer, idx) => (
              <div 
                key={idx} 
                className="mobile-list-item"
                onClick={() => navigate(`/m/customers/${customer.id}`)}
              >
                <div className="mobile-list-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>
                  <Building2 size={20} />
                </div>
                <div className="mobile-list-content">
                  <div className="mobile-list-title">{customer.name}</div>
                  <div className="mobile-list-subtitle">
                    {customer.org_number && <span>{customer.org_number}</span>}
                    {customer.city && <span> • {customer.city}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                    {customer.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b' }}>
                        <Mail size={12} />
                        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {customer.email}
                        </span>
                      </div>
                    )}
                    {customer.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b' }}>
                        <Phone size={12} />
                        {customer.phone}
                      </div>
                    )}
                  </div>
                </div>
                <ChevronRight size={20} color="#94a3b8" />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* FAB */}
      <button className="mobile-fab" onClick={() => navigate('/m/customers/new')}>
        <Plus size={24} />
      </button>
    </div>
  );
}
